/**
 * صفحه‌ی مشاوره — سه مرحله‌ی خط لوله.
 *
 * ترتیب مراحل از `state.stage` مشتق می‌شود، نه از یک شمارنده‌ی محلی. دلیل:
 * سرور صاحب مرحله است و اگر کلاینت خودش مرحله را نگه می‌داشت، دو منبع حقیقت
 * می‌شد و پس از بازسازی از هم واگرا می‌شدند.
 *
 * در حالت کارگاه (صفحه‌ی تست) این کامپوننت با `scope` صدا زده می‌شود و تب
 * اشکال‌زدایی هم اضافه می‌شود.
 */

import {
  Bug,
  Compass,
  Download,
  FileJson,
  FileSearch,
  FileText,
  Landmark,
  MessageSquare,
  Plus,
  Wand2,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Steps } from '@/components/ui/Steps'
import { Tabs, type TabItem } from '@/components/ui/Tabs'
import { Banner, Card, PageHeader, SectionHeader } from '@/components/ui/primitives'
import { downloadConsultation, type Scope } from '@/lib/api/endpoints'
import { saveBlob } from '@/lib/format'
import { useMeta } from '@/lib/meta-context'

import { CaseForm } from './CaseForm'
import { CaseView } from './CaseView'
import { ClarificationStep } from './ClarificationStep'
import { DebugPanel } from './DebugPanel'
import { EvidenceView } from './EvidenceView'
import { FeedbackBar } from './FeedbackBar'
import { RefineForm } from './RefineForm'
import { ResultView } from './ResultView'
import { TransitionPanel } from './TransitionPanel'
import { VerdictBand } from './VerdictBand'
import { useConsultation } from './useConsultation'

interface ConsultationPageProps {
  /** در حالت کارگاه لازم است؛ در نسخه‌ی مشتری تعریف نمی‌شود. */
  scope?: Scope
  /** پنل اشکال‌زدایی. تنها در حالت کارگاه. */
  showDebug?: boolean
}

export const ConsultationPage = ({ scope, showDebug = false }: ConsultationPageProps) => {
  const { meta } = useMeta()
  const consultation = useConsultation(scope)
  const { state } = consultation
  const [tab, setTab] = useState('result')

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Compass className="size-5.5" aria-hidden />}
        eyebrow="مشاوره‌ی تصمیم"
        title={meta.profile.app_title}
        {...(meta.profile.subtitle === '' ? {} : { caption: meta.profile.subtitle })}
        actions={
          state !== null && (
            <Button
              variant="secondary"
              icon={<Plus className="size-4" aria-hidden />}
              onClick={() => {
                consultation.reset()
                setTab('result')
              }}
            >
              تصمیم بعدی
            </Button>
          )
        }
      />

      {/* نوار مرحله جدا از سربرگ است تا در همه‌ی عرض‌ها یک خط کامل بگیرد و
          فشرده نشود. */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-line bg-surface-raised px-4 py-3 shadow-raised">
        <Steps current={consultation.step} />
      </div>

      {consultation.error !== null && (
        <Banner tone="danger" title="مشاوره کامل نشد">
          {consultation.error.message}
          {consultation.error.technicalDetail !== '' && (
            <pre className="mt-2 overflow-auto rounded-md bg-surface-sunken p-3 text-xs" dir="ltr">
              {consultation.error.technicalDetail}
            </pre>
          )}
        </Banner>
      )}

      {state === null && (
        <CaseForm onSubmit={consultation.start} isBusy={consultation.isBusy} />
      )}

      {state !== null && state.stage === 'awaiting_clarification' && state.analysis !== null && (
        <ClarificationStep
          questions={state.analysis.clarification_questions}
          prefill={state.analysis.clarification_questions.map((item) => {
            const retained = state.retained_answers.find(
              (answer) => answer.question === item.question,
            )
            return retained === undefined || retained.is_unknown ? null : retained.answer
          })}
          carriedCount={state.carried_facts.length}
          onSubmit={consultation.answer}
          onReset={consultation.reset}
          isBusy={consultation.isBusy}
        />
      )}

      {state !== null && state.stage === 'failed' && (
        <Card>
          <SectionHeader title="مشاوره ناتمام ماند" />
          <div className="space-y-4 p-5">
            <Banner tone="danger">
              {state.error_message ?? 'مشاوره به نتیجه نرسید.'}
            </Banner>
            {showDebug && state.error_detail !== null && (
              <pre className="overflow-auto rounded-md bg-surface-sunken p-3 text-xs" dir="ltr">
                {state.error_detail}
              </pre>
            )}
            <Button
              variant="primary"
              onClick={() => {
                consultation.reset()
              }}
            >
              شروع دوباره
            </Button>
          </div>
        </Card>
      )}

      {state !== null && state.stage === 'completed' && state.advice !== null && (
        <div className="space-y-4">
          <VerdictBand
            verdict={state.advice.verdict}
            direction={state.advice.conditional_direction}
            confidence={state.advice.confidence}
            attempt={state.attempt}
          />

          {state.audit_error !== null && (
            <Banner tone="warning">
              برآورد ساخته شد اما ثبت آن در لاگ حسابرسی انجام نشد؛ این مشاوره در آرشیو
              دیده نمی‌شود.
            </Banner>
          )}

          {/* پیش از tabها، چون در بازسازی نخستین پرسش کاربر این است: اصلاح من
              چه اثری داشت؟ */}
          {state.verdict_transition !== null && (
            <TransitionPanel
              transition={state.verdict_transition}
              ledger={state.condition_ledger}
            />
          )}

          <Tabs
            value={tab}
            onValueChange={setTab}
            items={buildTabs({
              state,
              showDebug,
              onRefine: consultation.refine,
              onFeedback: consultation.giveFeedback,
              isBusy: consultation.isBusy,
            })}
          />

          <ExportBar consultationId={state.consultation_id} />
        </div>
      )}
    </div>
  )
}

interface TabArgs {
  state: NonNullable<ReturnType<typeof useConsultation>['state']>
  showDebug: boolean
  onRefine: ReturnType<typeof useConsultation>['refine']
  onFeedback: ReturnType<typeof useConsultation>['giveFeedback']
  isBusy: boolean
}

const buildTabs = ({ state, showDebug, onRefine, onFeedback, isBusy }: TabArgs): TabItem[] => {
  const advice = state.advice
  if (advice === null) {
    return []
  }
  const tabs: TabItem[] = [
    {
      value: 'result',
      label: 'نتیجه',
      icon: <MessageSquare className="size-4" aria-hidden />,
      content: <ResultView advice={advice} supplementary={state.supplementary} />,
    },
    {
      value: 'evidence',
      label: 'شواهد',
      icon: <Landmark className="size-4" aria-hidden />,
      content: (
        <EvidenceView
          evidence={state.evidence}
          supplementary={state.supplementary}
          examples={advice.historical_examples}
        />
      ),
    },
    {
      value: 'case',
      label: 'پرونده',
      icon: <FileSearch className="size-4" aria-hidden />,
      content: (
        <CaseView
          consultationId={state.consultation_id}
          rawUserInput={state.raw_user_input}
          clarification={state.clarification}
        />
      ),
    },
    {
      value: 'refine',
      label: 'بازسازی',
      icon: <Wand2 className="size-4" aria-hidden />,
      content: <RefineForm state={state} onSubmit={onRefine} isBusy={isBusy} />,
    },
    {
      value: 'feedback',
      label: 'بازخورد',
      icon: <FileText className="size-4" aria-hidden />,
      content: <FeedbackBar onSubmit={onFeedback} />,
    },
  ]

  if (showDebug) {
    tabs.push({
      value: 'debug',
      label: 'اشکال‌زدایی',
      icon: <Bug className="size-4" aria-hidden />,
      content: <DebugPanel state={state} />,
    })
  }
  return tabs
}

/** خروجی‌گیری. هر دو قالب از مسیر پاک‌سازی راز عبور می‌کنند. */
const ExportBar = ({ consultationId }: { consultationId: string }) => {
  const [busy, setBusy] = useState<'markdown' | 'json' | null>(null)

  const save = async (format: 'markdown' | 'json'): Promise<void> => {
    setBusy(format)
    try {
      const { blob, filename } = await downloadConsultation(consultationId, format)
      saveBlob(blob, filename)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'دریافت خروجی انجام نشد.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface-sunken px-4 py-3">
      <span className="me-auto text-xs text-ink-muted">خروجی برای پیوست و بایگانی:</span>
      <Button
        size="sm"
        icon={<Download className="size-4" aria-hidden />}
        loading={busy === 'markdown'}
        onClick={() => {
          void save('markdown')
        }}
      >
        Markdown
      </Button>
      <Button
        size="sm"
        icon={<FileJson className="size-4" aria-hidden />}
        loading={busy === 'json'}
        onClick={() => {
          void save('json')
        }}
      >
        JSON
      </Button>
    </div>
  )
}
